import type {
  ScaleBand,
  ScaleLinear,
  ScaleLogarithmic,
  ScaleSymLog,
  ScaleTime,
} from 'd3-scale';

/** Union of possible tick value types returned by {@link getTicks}. */
export type TickValue = number | Date | string;

/** A scale that supports `ticks()` (continuous scales like linear, time, log). */
interface ScaleWithTicks {
  domain(): TickValue[];
  ticks(count?: number): TickValue[];
}

/** A scale that only exposes `domain()` (band and ordinal scales). */
interface ScaleWithDomainOnly {
  domain(): TickValue[];
}

/**
 * Extracts tick values from a d3 scale. Replaces `@visx/scale`'s `getTicks`.
 *
 * Continuous scales delegate to `scale.ticks(numTicks)`.
 * Band/ordinal scales sample evenly from `scale.domain()`.
 *
 * @param scale - Any d3 scale.
 * @param numTicks - Desired tick count hint. When omitted, band scales return the full domain.
 */
export function getTicks(
  scale:
    | ScaleLinear<number, number>
    | ScaleLogarithmic<number, number>
    | ScaleSymLog<number, number>,
  numTicks?: number,
): number[];

/** Returns `Date[]` for time scales. */
export function getTicks(
  scale: ScaleTime<number, number>,
  numTicks?: number,
): Date[];

/** Returns `string[]` for band scales. */
export function getTicks(scale: ScaleBand<string>, numTicks?: number): string[];

/** Fallback overload for duck-typed scale objects. */
export function getTicks(
  scale: ScaleWithTicks | ScaleWithDomainOnly,
  numTicks?: number,
): TickValue[];

export function getTicks(
  scale: ScaleWithTicks | ScaleWithDomainOnly,
  numTicks?: number,
): TickValue[] {
  if ('ticks' in scale && typeof scale.ticks === 'function') {
    return scale.ticks(numTicks);
  }

  return scale.domain().filter((_, index, arr) => {
    return (
      numTicks === undefined ||
      numTicks === null ||
      arr.length <= numTicks ||
      index % Math.round((arr.length - 1) / numTicks) === 0
    );
  });
}
